import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ImageIcon, ExternalLink, GripVertical } from 'lucide-react';
import MediaPickerModal from '@/components/MediaPickerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: '', subtitle: '', author: '', description: '', cover_image_url: '', lulu_url: '', order: 0, is_published: true };

export default function BooksManager() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // book object or 'new'
  const [form, setForm] = useState(EMPTY);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchBooks = async () => {
    try {
      const res = await axios.get(`${API}/admin/books-catalog`, { withCredentials: true });
      setBooks(res.data || []);
    } catch (e) {
      toast.error('Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBooks(); }, []);

  const openNew = () => { setForm({ ...EMPTY, order: books.length + 1 }); setEditing('new'); };
  const openEdit = (book) => { setForm({ ...EMPTY, ...book }); setEditing(book); };
  const closeForm = () => { setEditing(null); setForm(EMPTY); };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        subtitle: form.subtitle || null,
        author: form.author || null,
        description: form.description || null,
        cover_image_url: form.cover_image_url || null,
        lulu_url: form.lulu_url || null,
        order: parseInt(form.order) || 0,
        is_published: !!form.is_published,
      };
      if (editing === 'new') {
        await axios.post(`${API}/admin/books-catalog`, payload, { withCredentials: true });
        toast.success('Book created');
      } else {
        await axios.put(`${API}/admin/books-catalog/${editing.id}`, payload, { withCredentials: true });
        toast.success('Book updated');
      }
      closeForm();
      fetchBooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save book');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (book) => {
    if (!window.confirm(`Delete "${book.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/books-catalog/${book.id}`, { withCredentials: true });
      toast.success('Book deleted');
      fetchBooks();
    } catch (e) {
      toast.error('Failed to delete book');
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7] py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl retro-title text-[#6B4423]">Manage Books</h1>
            <button onClick={openNew} data-testid="add-book-button" className="retro-button flex items-center gap-2 bg-[#1B4B5A] hover:bg-[#143a45] text-white">
              <Plus className="w-4 h-4" /> Add Book
            </button>
          </div>

          <div className="admin-card">
            {loading ? (
              <p className="text-white text-center py-10">Loading…</p>
            ) : books.length === 0 ? (
              <p className="text-white/80 text-center py-10">No books yet. Click "Add Book" to create your first one.</p>
            ) : (
              <div className="space-y-3" data-testid="admin-books-list">
                {books.map((book) => (
                  <div key={book.id} className="flex items-center gap-4 bg-white border-2 border-[#8B6F47] rounded p-4">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <div className="w-12 h-16 rounded overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-100">
                      {book.cover_image_url && <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#6B4423] truncate">{book.title}</h3>
                      <p className="text-sm text-gray-600 truncate">{book.author} {book.subtitle ? `· ${book.subtitle}` : ''}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        <span className={`px-2 py-0.5 rounded-full font-semibold ${book.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {book.is_published ? 'Published' : 'Hidden'}
                        </span>
                        <span className="text-gray-500">Order: {book.order}</span>
                        {book.lulu_url && <a href={book.lulu_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 inline-flex items-center gap-1">Lulu <ExternalLink className="w-3 h-3" /></a>}
                      </div>
                    </div>
                    <button onClick={() => openEdit(book)} data-testid={`edit-book-${book.slug}`} className="retro-button text-sm bg-blue-600 hover:bg-blue-700 flex items-center gap-1">
                      <Edit className="w-4 h-4" /> Edit
                    </button>
                    <button onClick={() => remove(book)} data-testid={`delete-book-${book.slug}`} className="retro-button text-sm bg-red-600 hover:bg-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#6B4423] mb-6">{editing === 'new' ? 'Add Book' : 'Edit Book'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block font-bold text-[#6B4423] mb-1">Title *</label>
                <input data-testid="book-form-title" value={form.title} onChange={(e) => setField('title', e.target.value)} className="w-full border-2 border-gray-300 rounded p-2 text-gray-800" />
              </div>
              <div>
                <label className="block font-bold text-[#6B4423] mb-1">Subtitle</label>
                <input data-testid="book-form-subtitle" value={form.subtitle} onChange={(e) => setField('subtitle', e.target.value)} className="w-full border-2 border-gray-300 rounded p-2 text-gray-800" />
              </div>
              <div>
                <label className="block font-bold text-[#6B4423] mb-1">Author</label>
                <input data-testid="book-form-author" value={form.author} onChange={(e) => setField('author', e.target.value)} className="w-full border-2 border-gray-300 rounded p-2 text-gray-800" />
              </div>
              <div>
                <label className="block font-bold text-[#6B4423] mb-1">Description</label>
                <textarea data-testid="book-form-description" value={form.description} onChange={(e) => setField('description', e.target.value)} rows="4" className="w-full border-2 border-gray-300 rounded p-2 text-gray-800" />
              </div>
              <div>
                <label className="block font-bold text-[#6B4423] mb-1">Cover Image</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setShowPicker(true)} data-testid="book-form-choose-image" className="px-4 py-2 bg-[#1B4B5A] text-white rounded hover:bg-[#143a45] flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" /> {form.cover_image_url ? 'Change' : 'Choose Image'}
                  </button>
                  {form.cover_image_url && <img src={form.cover_image_url} alt="cover" className="w-16 h-20 object-cover rounded border-2 border-gray-300" />}
                </div>
              </div>
              <div>
                <label className="block font-bold text-[#6B4423] mb-1">Buy link (Lulu URL)</label>
                <input data-testid="book-form-lulu" value={form.lulu_url} onChange={(e) => setField('lulu_url', e.target.value)} placeholder="https://www.lulu.com/..." className="w-full border-2 border-gray-300 rounded p-2 text-gray-800" />
              </div>
              <div className="flex gap-4">
                <div className="w-32">
                  <label className="block font-bold text-[#6B4423] mb-1">Order</label>
                  <input data-testid="book-form-order" type="number" value={form.order} onChange={(e) => setField('order', e.target.value)} className="w-full border-2 border-gray-300 rounded p-2 text-gray-800" />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 font-bold text-[#6B4423]">
                    <input data-testid="book-form-published" type="checkbox" checked={form.is_published} onChange={(e) => setField('is_published', e.target.checked)} className="w-5 h-5" />
                    Published (visible to public)
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button onClick={save} disabled={saving} data-testid="book-form-save" className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-60">
                {saving ? 'Saving…' : 'Save Book'}
              </button>
              <button onClick={closeForm} className="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg font-bold hover:bg-gray-400">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <MediaPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(url) => { setField('cover_image_url', url); setShowPicker(false); }}
      />
    </Layout>
  );
}
